import React from "react";
import { Tag } from "antd";
import { isNil } from "lodash";
import { AiccPromptConfig } from "types/model/aiccPrompt";
import {
  EFFORT_LABEL,
  MODEL_DEPRECATED_FALLBACK_NOTE,
  READ_ONLY_DESCRIPTION,
  READ_ONLY_TITLE,
  TEMPERATURE_LABEL,
} from "../configs";
import { appliesTemperature } from "./appliesTemperature";
import { isModelRetired, modelAvailability } from "./availability";
import styles from "./ModelSettings.module.scss";

interface Props {
  config: AiccPromptConfig;
  modelName?: string;
  isDeprecated?: boolean;
  deprecationNote?: string;
  shutdownAt?: string;
}

/**
 * The settings of a copy nobody can edit here, with how long its model has left.
 *
 * A DEPRECATED MODEL IS NOT A BROKEN ONE, and this bar is read on the published copy — the one end
 * users are being served right now. So the deprecation appears as the model's remaining
 * availability rather than as a status badge: the badge said "DEPRECATED" beside a model answering
 * live traffic, which reads as an outage. `modelAvailability` owns the wording.
 *
 * Amber rather than red WHILE IT STILL ANSWERS. Red is for something to fix, and until the shutdown
 * date there is nothing to fix — this screen cannot even write. PAST that date it turns red, because
 * then the opposite is true: the vendor has stopped serving the model and this prompt, the one end
 * users are on, fails on its next turn.
 */
function ReadOnlySettingsBar({ config, modelName, isDeprecated, deprecationNote, shutdownAt }: Props) {
  const availability = modelAvailability({ isDeprecated, shutdownAt });
  const retired = isModelRetired({ isDeprecated, shutdownAt });
  const badgeClass = retired ? styles.modelBadgeRetired : styles.modelBadgeAging;
  return (
    <div className={styles.readOnlyBar}>
      <div className={styles.readOnlyFacts}>
        <div className={styles.settingsGroup}>
          <span className={styles.settingsLabel}>{READ_ONLY_TITLE}</span>
          <Tag className={isDeprecated ? badgeClass : styles.modelBadge}>{modelName || config.modelId}</Tag>
          {!!availability && (
            <span className={retired ? styles.retiredLabel : styles.availabilityLabel}>{availability}</span>
          )}
        </div>
        {!isNil(config.temperature) && appliesTemperature(config.reasoningEffort) && (
          <span className={styles.settingsLabel}>{`${TEMPERATURE_LABEL}: ${config.temperature.toFixed(1)}`}</span>
        )}
        {!!config.reasoningEffort && (
          <span className={styles.settingsLabel}>{`${EFFORT_LABEL}: ${config.reasoningEffort}`}</span>
        )}
      </div>
      {isDeprecated && (
        // No "is deprecated" lead-in: the availability above already stated the status, and saying
        // it twice is what made the model look unusable. This line carries only the vendor's reason.
        <span className={styles.readOnlyNote}>{deprecationNote || MODEL_DEPRECATED_FALLBACK_NOTE}</span>
      )}
      <span className={styles.readOnlyNote}>{READ_ONLY_DESCRIPTION}</span>
    </div>
  );
}

export default ReadOnlySettingsBar;
